import type { Vehicle } from '../../data/vehicles'
import { VehicleCardSkeleton } from '../ui/Skeleton'
import { useGsapStagger } from '../../hooks/useGsapReveal'
import VehicleCard from './VehicleCard'

type VehicleGridProps = {
  vehicles: Vehicle[]
  loading?: boolean
  /** How many skeletons to hold the space with while loading. */
  placeholders?: number
  columns?: 2 | 3
  saved?: string[]
  onToggleSave?: (id: string) => void
  className?: string
}

/* The responsive grid, plus the reveal. Cards arrive from the data layer
   after mount, so the stagger is keyed on the count rather than firing once
   against an empty container. */
function VehicleGrid({
  vehicles,
  loading = false,
  placeholders = 6,
  columns = 3,
  saved = [],
  onToggleSave,
  className = '',
}: VehicleGridProps) {
  const ref = useGsapStagger<HTMLUListElement>(
    '[data-card]',
    { y: 30, stagger: 0.07 },
    [vehicles.length],
  )

  const columnClass =
    columns === 2
      ? 'grid-cols-1 sm:grid-cols-2'
      : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'

  if (loading) {
    return (
      <ul className={`grid gap-6 ${columnClass} ${className}`}>
        {Array.from({ length: placeholders }, (_, index) => (
          <li key={index}>
            <VehicleCardSkeleton />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <ul ref={ref} className={`grid gap-6 ${columnClass} ${className}`}>
      {vehicles.map((vehicle) => (
        <li key={vehicle.id} data-card>
          <VehicleCard
            vehicle={vehicle}
            savedIds={saved}
            onToggleSave={onToggleSave}
          />
        </li>
      ))}
    </ul>
  )
}

export default VehicleGrid
